import { isDefined } from '@sgme/fp';
import { useSPIntl } from 'hooks/useIntl';
import type { FormError, TranslatedValidationError } from 'models/formError';
import { useContext } from 'react';
import { areLegacyInvalidFields } from 'state/selectors/meta/static/utils';
import { translateValidationError } from 'utils/errors/form-validation-errors';

import { InvalidFieldsContext } from '../contexts/InvalidFieldsContext';

export function getFirstVisibleValidationError(
  fieldValidationsErrors: readonly FormError[] | undefined,
): FormError | undefined {
  return fieldValidationsErrors?.find(validationError => !validationError.isHidden);
}

export function useFieldValidationError(fieldId?: string, prefix = ''): TranslatedValidationError | undefined {
  const invalidFields = useContext(InvalidFieldsContext);
  const intl = useSPIntl();
  if (fieldId === undefined) {
    return;
  }
  if (!invalidFields || areLegacyInvalidFields(invalidFields)) {
    return undefined;
  } else {
    const fieldValidationsErrors = invalidFields[fieldId];

    const firstValidationError = fieldValidationsErrors && fieldValidationsErrors[0];
    return firstValidationError && !firstValidationError.isHidden
      ? translateValidationError(firstValidationError, intl, prefix)
      : undefined;
  }
}

export function useFieldValidationErrors(fieldId?: string, prefix = ''): TranslatedValidationError[] {
  const invalidFields = useContext(InvalidFieldsContext);
  const intl = useSPIntl();
  if (fieldId === undefined || areLegacyInvalidFields(invalidFields) || !invalidFields[fieldId]) {
    return [];
  }
  return invalidFields[fieldId]
    .map(validationError => translateValidationError(validationError, intl, prefix))
    .filter(isDefined);
}
